package com.nonopn.skywatcher.common.app.pages.map.model.weather

import androidx.compose.ui.graphics.Color
import com.nonopn.skywatcher.common.app.res.Res
import com.nonopn.skywatcher.common.app.res.aqi_index_good
import com.nonopn.skywatcher.common.app.res.aqi_index_hazardous
import com.nonopn.skywatcher.common.app.res.aqi_index_moderate
import com.nonopn.skywatcher.common.app.res.aqi_index_unhealthy
import com.nonopn.skywatcher.common.app.res.aqi_index_unhealthy_sensitive_group
import com.nonopn.skywatcher.common.app.res.aqi_index_very_unhealthy
import com.nonopn.skywatcher.design.theme.AppColor
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import org.jetbrains.compose.resources.StringResource

/**
 * Available here
 *
 * https://www.xweather.com/docs/weather-api/endpoints/airquality#examples
 */
@Serializable
data class AirQuality(
    val id: String? = null,
    val loc: AirQualityLatLng,
    // val place: Place,
    val periods: List<AirQualityPeriod>
)

@Serializable
data class AirQualityPeriod(
    val timestamp: Long,
    /**
     * The standardized Air Quality Index value from 0 - 500.
     *
     * https://www.airnow.gov/aqi/aqi-basics/
     */
    val aqi: Int,
    /**
     * The Air Quality category based on the AQI:
     * See AirNow
     *
     * - good
     * - moderate
     * - usg (Unhealthy for Sensitive Groups)
     * - unhealthy
     * - very Unhealthy
     * - hazardous
     *
     * If using filter=china, the following categories are utilized:
     * More info on China AQI / Categories.
     *
     * - excellent
     * - good
     * - slightly polluted
     * - lightly polluted
     * - moderately polluted
     * - severely polluted
     *
     * If using the filter=india, the following categories are utilizes:
     * More info on India AQI / Categories.
     *
     * - good
     * - satisfactory
     * - moderately polluted
     * - poor
     * - very poor
     * - severe
     */
    val category: String,
    /**
     * The 6 character hexadecimal color code for the specific category.
     */
    val color: String,
    /**
     * The method used for the AQI calculation:
     * - airnow = Used the EPA AirNow AQI specification (default)
     * - china = Used the China AQI specification (filter=china)
     * - india = Used the India AQI specification (filter=india)
     */
    val method: String,
    /**
     * The dominant pollutant. Normally set to one of the following:
     * - co (Carbon Monoxide)
     * - no2 (Nitrogen Dioxide)
     * - o3 (Ozone)
     * - pm10 (Particle Matter <10µm)
     * - pm2.5 (Particle Matter <2.5µm)
     * - so2 (Sulfur Dioxide)
     */
    val dominant: String,
    val health: AirQualityHealth,
    val pollutants: List<AirQualityPollutant>,
    // val profile: Profile
) {
    val actualAQI: AQI
        get() {
            val found = AQI.entries.firstOrNull { it.threshold > aqi }

            return found ?: AQI.Hazardous
        }
}

@Serializable
data class AirQualityPollutant(
    val type: String,
    val name: String,
    /**
     * The pollutant measurement in parts per billion. Null if this unit is not utilized
     *
     * pm2.5 & pm10 do not utilize PPB.
     */
    val valuePPB: Int? = null,
    /**
     * The pollutant measurement in parts per micrograms per cubic meter. Null if this unit is not utilized
     */
    val valueUGM3: Float? = null,
    /**
     * The pollutant measurement converted to the common AQI value, from 0 to 500 if using AirNow AQI.
     */
    val aqi: Int,
    /**
     * The Air Quality category based on the AQI:
     * See AirNow
     *
     * - good
     * - moderate
     * - usg (Unhealthy for Sensitive Groups)
     * - unhealthy
     * - very Unhealthy
     * - hazardous
     *
     * If using filter=china, the following categories are utilized:
     * More info on China AQI / Categories.
     *
     * - excellent
     * - good
     * - slightly polluted
     * - lightly polluted
     * - moderately polluted
     * - severely polluted
     *
     * If using the filter=india, the following categories are utilizes:
     * More info on India AQI / Categories.
     *
     * - good
     * - satisfactory
     * - moderately polluted
     * - poor
     * - very poor
     * - severe
     */
    val category: PollutantCategory,
    /**
     * The 6 character hexadecimal color code for the specific category.
     *
     * https://www.airnow.gov/aqi/aqi-basics/
     */
    val color: String,
    /**
     * The method used for the AQI calculation:
     * - airnow = Used the EPA AirNow AQI specification (default)
     * - china = Used the China AQI specification (filter=china)
     * - india = Used the India AQI specification (filter=india)
     */
    val method: PollutantMethod
) {
    fun pollutant() = Pollutant.entries.firstOrNull { type.lowercase() == it.type }

    val actualAQI: AQI
        get() {
            val found = AQI.entries.firstOrNull { it.threshold > aqi }

            return found ?: AQI.Hazardous
        }
}

@Serializable
enum class AQI(
    override val threshold: Int,
    override val color: Color,
    override val textColor: Color,
    override val colorSub: Color,
    override val text: StringResource
) : DisplayableIndex {
    Good(
        50, AppColor.Green,
        AppColor.WhiteCream,
        AppColor.GreenSemiTransparent,
        Res.string.aqi_index_good
    ),
    Moderate(
        100,
        AppColor.Yellorange,
        AppColor.WhiteCream,
        AppColor.YellorangeSemiTransparent,
        Res.string.aqi_index_moderate
    ),
    UnhealthySensitiveGroup(
        150,
        AppColor.Orange,
        AppColor.WhiteCream,
        AppColor.OrangeSemiTransparent,
        Res.string.aqi_index_unhealthy_sensitive_group
    ),
    Unhealthy(
        200,
        AppColor.Red,
        AppColor.WhiteCream,
        AppColor.RedSemiTransparent,
        Res.string.aqi_index_unhealthy
    ),
    VeryUnhealthy(
        300,
        AppColor.RedDark,
        AppColor.WhiteCream,
        AppColor.RedDarkSemiTransparent,
        Res.string.aqi_index_very_unhealthy
    ),
    Hazardous(
        500,
        AppColor.RedDarker,
        AppColor.WhiteCream,
        AppColor.RedDarkerSemiTransparent,
        Res.string.aqi_index_hazardous
    )
}

@Serializable
enum class PollutantMethod {
    @SerialName("airnow")
    AirNow,

    @SerialName("china")
    China,

    @SerialName("india")
    India
}

@Serializable
enum class PollutantCategory {
    @SerialName("excellent")
    Excellent, // china only

    @SerialName("good")
    Good,

    @SerialName("satisfactory")
    Satisfactory, // India

    @SerialName("slightly polluted")
    SlightlyPolluted, // china only

    @SerialName("moderate")
    Moderate,

    @SerialName("lightly polluted")
    LightlyPolluted, // china only

    @SerialName("usg")
    UnhealthySensitiveGroups,

    @SerialName("moderatly polluted")
    ModeratlyPolluted, // china and india only

    @SerialName("unhealthy")
    Unhealthy,

    @SerialName("poor")
    Poor, // india only

    @SerialName("very unhealthy")
    VeryUnhealthy,

    @SerialName("very poor")
    VeryPoor, // India only

    @SerialName("severed")
    Severe, // India only

    @SerialName("severely polluted")
    SeverelyPolluted, // china only

    @SerialName("hazardous")
    Hazardous,
}

enum class Pollutant(internal val type: String) {
    CO("co"),
    NO2("no2"),
    O3("o3"),
    PM1("pm1"),
    PM10("pm10"),
    PM2_5("pm2.5"),
    SO2("so2")
}

@Serializable
data class AirQualityHealth(
    /**
     * The air quality health index. A simplified global health index using a scale of 0 to 20 to describe the effect of air quality on health
     *
     * - low: 0 - 3
     * - moderate: 4-6
     * - high: 7-10
     * - very high: > 10
     */
    val index: Int,
    /**
     * The air quality health index category name:
     *
     * - low: 0 - 3
     * - moderate: 4-6
     * - high: 7-10
     * - very high: > 10
     */
    val category: String,
    /**
     * The air quality health index category color:
     * The air quality health index. A simplified global health index using a scale of 0 to 20 to describe the effect of air quality on health
     *
     * - low: #0000FF
     * - moderate: #00FF00
     * - high: #FFFF00
     * - very high: #FF0000
     */
    val color: String
) {
    fun indexScale(): AirQualityHealthIndex {
        return when {
            index < 3 -> AirQualityHealthIndex.Low
            index <= 6 -> AirQualityHealthIndex.Moderate
            index <= 10 -> AirQualityHealthIndex.High
            index > 10 -> AirQualityHealthIndex.VeryHigh
            else -> AirQualityHealthIndex.Low
        }
    }
}

enum class AirQualityHealthIndex {
    Low,
    Moderate,
    High,
    VeryHigh
}

@Serializable
data class AirQualityLatLng(
    @SerialName("lat")
    val latitude: Double,
    @SerialName("long")
    val longitude: Double
)